/**
 * EventCheckinView — Check-in on QR scan
 *
 * On mount, tries auto-check-in via cookie (credentials: include).
 * If that fails, shows an email form so the user can identify themselves.
 *
 * @story SCRUM-1450 — 0.1.1.3-02: Render Directive Frontend Integration
 * @bug SCRUM-1925 — email fallback when cookie absent
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

function postCheckin(intentCode, email) {
  const body = { intent_code: intentCode };
  if (email) {
    body.email = email.trim().toLowerCase();
  }
  return fetch(`${API_BASE_URL}/api/v1/events/checkin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

export default function EventCheckinView({ event, intentCode, marketSlug: _marketSlug }) {
  // loading → (auto-fire) → success | email_form → submitting → success | error
  const [status, setStatus] = useState('loading');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!intentCode) {
      setStatus('email_form');
      return;
    }

    postCheckin(intentCode)
      .then(res => {
        if (res.ok) {
          setStatus('success');
        } else {
          setStatus('email_form');
        }
      })
      .catch(() => {
        setStatus('email_form');
      });
  }, [intentCode]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      return;
    }

    setStatus('submitting');

    try {
      const res = await postCheckin(intentCode, email);
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Checking you in...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-emerald-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="text-emerald-500 text-5xl mb-4" aria-hidden="true">&#10003;</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            You&apos;re checked in!
          </h1>
          <p className="text-gray-600">
            Welcome to {event?.name || 'the event'}.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'email_form' || status === 'submitting') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-900 via-emerald-800 to-gray-900 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {event?.name || 'Event Check-In'}
          </h1>
          <p className="text-gray-600 text-sm mb-4">
            Enter the email you used to RSVP.
          </p>
          <form onSubmit={handleEmailSubmit} className="space-y-3">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              aria-label="Email"
              required
              autoFocus
            />
            <button
              type="submit"
              disabled={status === 'submitting' || !email.trim()}
              className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium
                         hover:bg-emerald-700 transition-colors
                         disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? 'Checking in...' : 'Check In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // status === 'error'
  return (
    <div className="min-h-screen flex items-center justify-center bg-amber-50 px-4">
      <div className="max-w-md w-full text-center">
        <p className="text-amber-800 font-medium mb-2">Check-in failed</p>
        <p className="text-gray-600 text-sm mb-4">
          We couldn&apos;t find your RSVP. Please see event staff for assistance.
        </p>
        <button
          onClick={() => setStatus('email_form')}
          className="text-emerald-600 text-sm font-medium hover:underline"
        >
          Try a different email
        </button>
      </div>
    </div>
  );
}

EventCheckinView.propTypes = {
  event: PropTypes.shape({
    name: PropTypes.string,
    starts_at: PropTypes.string,
    location_name: PropTypes.string,
  }),
  intentCode: PropTypes.string,
  marketSlug: PropTypes.string,
};
